import { Link, SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { useEffect, useState } from 'react';

import { WonItemRow } from '@/pages/WonItems/components/WonItemsCard/components/WonItemRow/WonItemRow';
import { LocationInfo } from '@/redux/modules/wonItems/wonItems.types';
import { WonCatalog } from '@/types/WonCatalog';
import { WonItemInfo } from '@/types/WonItem';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getGoogleMapUrlFromAddress } from '@/utils/urls';

type Props = {
    locationInfo: LocationInfo;
    wonCatalog: WonCatalog;
};

export const WonItemLocation = ({ locationInfo, wonCatalog }: Props) => {
    const formatMessage = useAppFormatMessage();
    const formattedAddress = `${locationInfo.city}, ${locationInfo.region} ${locationInfo.country}`;

    const [wonItems, setWonItems] = useState<WonItemInfo[]>([]);

    const [mapsUrl, setMapsUrl] = useState('');

    useEffect(() => {
        setMapsUrl(
            getGoogleMapUrlFromAddress({
                city: locationInfo.city,
                country: locationInfo.country,
                region: locationInfo.region,
            })
        );
    }, [locationInfo]);

    useEffect(() => {
        setWonItems(
            wonCatalog.items.map((item) => ({
                ...item,
                catalogId: wonCatalog.catalogId,
                currency: wonCatalog.currency,
                sellerId: wonCatalog.sellerId,
            }))
        );
    }, [wonCatalog]);

    let shippingInfo: JSX.Element = null;
    if (locationInfo.shippingStatus) {
        let subtitle: JSX.Element = null;
        // additional information shown/hidden based on available data
        if (locationInfo.carrierName) {
            if (locationInfo.shippingTrackingLink) {
                // shipped via {carrier}. view tracking link
                subtitle = (
                    <div className="hui-text-body-secondary text-text-secondary">
                        {locationInfo.shippingStatusSecondary}{' '}
                        {formatMessage(['wonitems', 'itemsByLocation', 'shippedBy'], {
                            values: {
                                carrier: locationInfo.carrierName,
                            },
                        })}{' '}
                        <Link
                            href={locationInfo.shippingTrackingLink}
                            size="sm"
                        >
                            {formatMessage(['wonitems', 'itemsByLocation', 'viewTrackingHistory'])}
                        </Link>
                    </div>
                );
            } else if (locationInfo.shippingTrackingNumber) {
                // shipped via {carrier}. Tracking number: 32508235
                subtitle = (
                    <div className="hui-text-body-secondary text-text-secondary">
                        {locationInfo.shippingStatusSecondary}{' '}
                        {formatMessage(['wonitems', 'itemsByLocation', 'shippedBy'], {
                            values: {
                                carrier: locationInfo.carrierName,
                            },
                        })}{' '}
                        {formatMessage(['wonitems', 'itemsByLocation', 'trackingNumber'], {
                            values: {
                                trackingNumber: locationInfo.shippingTrackingNumber,
                            },
                        })}
                    </div>
                );
            } else {
                // shipped via {carrier}
                subtitle = (
                    <div className="hui-text-body-secondary text-text-secondary">
                        {locationInfo.shippingStatusSecondary}{' '}
                        {formatMessage(['wonitems', 'itemsByLocation', 'shippedBy'], {
                            values: {
                                carrier: locationInfo.carrierName,
                            },
                        })}
                    </div>
                );
            }
        } else if (locationInfo.shippingTrackingLink) {
            // view tracking link
            subtitle = (
                <div className="hui-text-body-secondary text-text-secondary">
                    {locationInfo.shippingStatusSecondary}{' '}
                    <Link
                        href={locationInfo.shippingTrackingLink}
                        size="sm"
                    >
                        {formatMessage(['wonitems', 'itemsByLocation', 'viewTrackingHistory'])}
                    </Link>
                </div>
            );
        } else {
            // otherwise, nothing else is shown
            subtitle = (
                <div className="hui-text-body-secondary text-text-secondary">
                    {locationInfo.shippingStatusSecondary}
                </div>
            );
        }

        shippingInfo = (
            <>
                <H5>{locationInfo.shippingStatus}</H5>
                {subtitle}
            </>
        );
    }

    return (
        <section>
            {shippingInfo}
            <div className="hui-text-body-secondary text-text-secondary">
                {formatMessage(['wonitems', 'itemsByLocation', 'location'], {
                    values: {
                        itemCount: locationInfo.itemIds.length,
                        locationLink: (
                            <SubtleLink
                                key="won-items-by-location-map-link"
                                href={mapsUrl}
                                size="sm"
                                target="_blank"
                            >
                                {formattedAddress}
                            </SubtleLink>
                        ),
                    },
                })}
            </div>
            <div>
                {locationInfo.itemIds.map((itemId) => {
                    const item = wonItems.find((item) => item.itemId === itemId);
                    if (item) {
                        return (
                            <div
                                className="mt-lg"
                                key={item.itemId}
                            >
                                <WonItemRow
                                    buyersPremium={wonCatalog.buyersPremium}
                                    item={item}
                                    key={itemId}
                                    won
                                />
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        </section>
    );
};
